import { useEffect, useState } from 'react';
import { apiUrl } from '../../shared/api/apiConfig';
import { formatDisplayDateTime } from '../../shared/utils/dateFormat';
import './DevicesPage.css';

const DEVICE_TYPES = ['Scanner', 'Scale'];
const DEVICE_STATUSES = ['Active', 'Inactive', 'Issue', 'Maintenance'];

const EMPTY_FORM = {
  deviceId: '',
  deviceType: 'Scanner',
  name: '',
  deviceStatus: 'Active',
};

function sortDevices(devices) {
  return [...devices].sort((left, right) => left.deviceId.localeCompare(right.deviceId));
}

function formatDate(value) {
  return formatDisplayDateTime(value, 'ยังไม่เคย');
}

function maskProvisionedKey(value) {
  if (!value) {
    return 'ไม่สามารถใช้งานได้ในเซสชันนี้';
  }

  return '•'.repeat(Math.max(value.length, 12));
}

export default function DevicesPage() {
  const [devices, setDevices] = useState([]);
  const [bindings, setBindings] = useState([]);
  const [provisionedKeys, setProvisionedKeys] = useState({});
  const [revealedKeys, setRevealedKeys] = useState({});
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [loadState, setLoadState] = useState({ type: 'idle', message: '' });
  const [saveState, setSaveState] = useState({ type: 'idle', message: '' });

  const assignmentByDeviceId = {};
  for (const binding of bindings) {
    if (binding.status !== 'Active') {
      continue;
    }

    assignmentByDeviceId[binding.scannerDeviceId] = {
      registerId: binding.registerId,
      role: 'Scanner',
    };
    assignmentByDeviceId[binding.scaleDeviceId] = {
      registerId: binding.registerId,
      role: 'Scale',
    };
  }

  const editingAssignment = editingId ? assignmentByDeviceId[editingId] ?? null : null;
  const visibleDevices = statusFilter === 'All'
    ? devices
    : devices.filter((device) => device.deviceStatus === statusFilter);
  const scannerCount = devices.filter((device) => device.deviceType === 'Scanner').length;
  const scaleCount = devices.filter((device) => device.deviceType === 'Scale').length;
  const isPageLoading = loadState.type === 'pending';
  const pageLoadingMessage = loadState.message || 'Loading devices and assignments...';

  useEffect(() => {
    let active = true;

    async function loadPageData() {
      setLoadState({ type: 'pending', message: 'Loading devices and assignments...' });

      try {
        const [devicesResponse, bindingsResponse] = await Promise.all([
          fetch(apiUrl('/devices')),
          fetch(apiUrl('/registerbindings')),
        ]);

        if (!devicesResponse.ok || !bindingsResponse.ok) {
          throw new Error('Failed to load devices.');
        }

        const [devicesPayload, bindingsPayload] = await Promise.all([
          devicesResponse.json(),
          bindingsResponse.json(),
        ]);
        if (!active) {
          return;
        }

        setDevices(sortDevices(devicesPayload ?? []));
        setBindings(bindingsPayload ?? []);
        setLoadState({ type: 'success', message: '' });
      } catch (error) {
        if (!active) {
          return;
        }

        setLoadState({
          type: 'error',
          message: error instanceof Error ? error.message : 'Failed to load devices.',
        });
      }
    }

    loadPageData();

    return () => {
      active = false;
    };
  }, []);

  function handleFieldChange(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  function handleEdit(device) {
    setEditingId(device.deviceId);
    setForm({
      deviceId: device.deviceId ?? '',
      deviceType: device.deviceType ?? 'Scanner',
      name: device.name ?? '',
      deviceStatus: device.deviceStatus ?? 'Active',
    });
    setSaveState({ type: 'idle', message: '' });
  }

  function handleReset(clearFeedback = true) {
    setEditingId('');
    setForm(EMPTY_FORM);
    if (clearFeedback) {
      setSaveState({ type: 'idle', message: '' });
    }
  }

  function getAssignmentState(deviceId) {
    return assignmentByDeviceId[deviceId] ?? null;
  }

  function getAssignmentLabel(assignment) {
    return assignment ? 'เชื่อมโยงแล้ว' : 'ยังไม่เชื่อมโยง';
  }

  function getThaiRoleLable(assignment) {
    if (!assignment) {
      return 'ไม่ทราบประเภทอุปกรณ์';
    }

    switch (assignment.role) {
      case 'Scanner':
        return 'เครื่องสแกน';
      case 'Scale':
        return 'เครื่องชั่ง';
      default:
        return 'ไม่ทราบประเภทอุปกรณ์';
    }
  }

  function getAssignmentDescription(assignment) {
    return assignment
      ? `${getThaiRoleLable(assignment)} เชื่อมโยงกับสถานี ${assignment.registerId}`
      : 'ไม่เชื่อมโยงกับสถานีที่ใช้งานอยู่';
  }

  function toggleKeyReveal(deviceId) {
    setRevealedKeys((current) => ({
      ...current,
      [deviceId]: !current[deviceId],
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const payload = {
      deviceId: form.deviceId.trim(),
      deviceType: form.deviceType,
      name: form.name.trim(),
      deviceStatus: form.deviceStatus,
    };

    if (!payload.deviceId || !payload.name) {
      setSaveState({ type: 'error', message: 'ต้องกำหนดรหัสอุปกรณ์และชื่ออุปกรณ์' });
      return;
    }

    const isEditing = Boolean(editingId);
    setSaveState({
      type: 'pending',
      message: isEditing ? 'กำลังอัปเดตอุปกรณ์...' : 'กำลังสร้างอุปกรณ์...',
    });

    try {
      const response = await fetch(
        isEditing ? apiUrl(`/devices/${encodeURIComponent(editingId)}`) : apiUrl('/devices'),
        {
          method: isEditing ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.message ?? 'ไม่สามารถบันทึกอุปกรณ์ได้');
      }

      const responsePayload = await response.json();
      const saved = responsePayload.deviceId ? responsePayload : responsePayload.device;
      setDevices((current) => {
        const next = current.filter((device) => device.deviceId !== saved.deviceId);
        next.push(saved);
        return sortDevices(next);
      });
      if (responsePayload.provisionedKey) {
        setProvisionedKeys((current) => ({
          ...current,
          [saved.deviceId]: responsePayload.provisionedKey,
        }));
        setRevealedKeys((current) => ({
          ...current,
          [saved.deviceId]: false,
        }));
      }

      setSaveState({
        type: 'success',
        message: responsePayload.provisionedKey
          ? `บันทึกอุปกรณ์เรียบร้อยแล้ว คีย์อุปกรณ์ใหม่สามารถดูได้ด้านล่างและถูกซ่อนไว้โดยค่าเริ่มต้น`
          : isEditing
            ? 'อัปเดตอุปกรณ์เรียบร้อยแล้ว'
            : 'สร้างอุปกรณ์เรียบร้อยแล้ว',
      });
      handleReset(false);
    } catch (error) {
      setSaveState({
        type: 'error',
        message: error instanceof Error ? error.message : 'ไม่สามารถบันทึกอุปกรณ์ได้',
      });
    }
  }

  async function handleDelete(deviceId) {
    const confirmed = window.confirm(`ลบอุปกรณ์ '${deviceId}'?`);
    if (!confirmed) {
      return;
    }

    setSaveState({ type: 'pending', message: `กำลังลบอุปกรณ์ '${deviceId}'...` });

    try {
      const response = await fetch(apiUrl(`/devices/${encodeURIComponent(deviceId)}`), {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error((await response.json().catch(() => ({}))).message ?? '');
      }

      setDevices((current) => current.filter((device) => device.deviceId !== deviceId));
      if (editingId === deviceId) {
        handleReset();
      }
      setSaveState({ type: 'success', message: `ลบอุปกรณ์ '${deviceId}' เรียบร้อยแล้ว` });
    } catch (error) {
      setSaveState({
        type: 'error',
        message: error instanceof Error ? error.message : 'ไม่สามารถลบอุปกรณ์ได้',
      });
    }
  }

  async function handleRotateKey(deviceId) {
    const confirmed = window.confirm(`หมุนคีย์อุปกรณ์สำหรับ '${deviceId}'? คีย์ปัจจุบันจะหยุดทำงาน.`);

    if (!confirmed) {
      return;
    }

    setSaveState({ type: 'pending', message: `กำลังหมุนคีย์อุปกรณ์สำหรับ '${deviceId}'...` });

    try {
      const response = await fetch(apiUrl(`/devices/${encodeURIComponent(deviceId)}/rotate-key`), {
        method: 'POST',
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.message ?? 'ไม่สามารถหมุนคีย์อุปกรณ์ได้');
      }

      const payload = await response.json();
      setDevices((current) => {
        const next = current.filter((device) => device.deviceId !== payload.deviceId);
        next.push(payload);
        return sortDevices(next);
      });
      setProvisionedKeys((current) => ({
        ...current,
        [payload.deviceId]: payload.provisionedKey,
      }));
      setRevealedKeys((current) => ({
        ...current,
        [payload.deviceId]: false,
      }));
      setSaveState({
        type: 'success',
        message: `สร้างคีย์ใหม่สำหรับ '${deviceId}' เรียบร้อยแล้ว คีย์ถูกซ่อนไว้ด้านล่างจนกว่าคุณจะเปิดเผย`,
      });
    } catch (error) {
      setSaveState({
        type: 'error',
        message: error instanceof Error ? error.message : 'ไม่สามารถหมุนคีย์อุปกรณ์ได้',
      });
    }
  }

  return (
    <section className="devices-page">
      <div className="devices-hero">
        <div>
          <p className="devices-eyebrow">การตั้งค่าอุปกรณ์</p>
          <h1>จัดการอุปกรณ์สำหรับชั่งน้ำหนัก</h1>
          <p className="devices-subtitle">
            สร้างและแก้ไขเครื่องสแกนและเครื่องชั่งที่ใช้ในกระบวนการชั่งน้ำหนัก รวมถึงการดูสถานะการเชื่อมต่อและการจัดการคีย์สำหรับอุปกรณ์แต่ละเครื่อง
          </p>
        </div>
        <div className="devices-badge-row">
          <div className="devices-badge">
            <strong>{scannerCount}</strong>
            <span>เครื่องสแกน</span>
          </div>
          <div className="devices-badge">
            <strong>{scaleCount}</strong>
            <span>เครื่องชั่ง</span>
          </div>
        </div>
      </div>

      {isPageLoading ? (
        <div className="devices-loading-block" role="status" aria-live="polite">
          <div className="devices-loading-spinner" aria-hidden="true" />
          <p>{pageLoadingMessage}</p>
        </div>
      ) : (
      <div className="devices-grid">
        <form className="devices-card devices-form-card" onSubmit={handleSubmit}>
          <div className="devices-card-header">
            <div>
              <h2>{editingId ? 'รายละเอียดอุปกรณ์' : 'เพิ่มอุปกรณ์ใหม่'}</h2>
              <p>{editingId ? `กำลังตรวจสอบ ${editingId}` : 'ตั้งค่าอุปกรณ์ใหม่สำหรับกระบวนการชั่งน้ำหนัก'}</p>
            </div>
          </div>

          {editingId ? (
            <div className="devices-detail-summary">
              <div className="devices-pill-row">
                <span className={`devices-status devices-status-${String(form.deviceStatus).toLowerCase()}`}>
                  {form.deviceStatus}
                </span>
                <span className={`devices-assignment devices-assignment-${editingAssignment ? 'assigned' : 'unassigned'}`}>
                  {getAssignmentLabel(editingAssignment)}
                </span>
              </div>
              <p>{getAssignmentDescription(editingAssignment)}</p>
              <div className="devices-key-panel">
                <span className="devices-key-label">Device key</span>
                <code className="devices-key-value">
                  {revealedKeys[editingId]
                    ? (provisionedKeys[editingId] ?? 'Not available for this session')
                    : maskProvisionedKey(provisionedKeys[editingId])}
                </code>
                {provisionedKeys[editingId] ? (
                  <button
                    type="button"
                    className="devices-link-btn"
                    onClick={() => toggleKeyReveal(editingId)}
                  >
                    {revealedKeys[editingId] ? 'ซ่อนคีย์' : 'เปิดเผยคีย์'}
                  </button>
                ) : (
                  <span className="devices-key-help">การเปิดเผยคีย์จะสามารถทำได้หลังจากสร้างหรือหมุนคีย์ในเซสชันนี้</span>
                )}
              </div>
            </div>
          ) : null}

          <label className="devices-field">
            <span>รหัสอุปกรณ์</span>
            <input
              name="deviceId"
              value={form.deviceId}
              onChange={handleFieldChange}
              placeholder="เช่น SCAN-01"
              disabled={Boolean(editingId)}
            />
          </label>

          <div className="devices-form-row">
            <label className="devices-field">
              <span>ประเภทอุปกรณ์</span>
              <select name="deviceType" value={form.deviceType} onChange={handleFieldChange}>
                {DEVICE_TYPES.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>

            <label className="devices-field">
              <span>สถานะ</span>
              <select name="deviceStatus" value={form.deviceStatus} onChange={handleFieldChange}>
                {DEVICE_STATUSES.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="devices-field">
            <span>ชื่อ</span>
            <input
              name="name"
              value={form.name}
              onChange={handleFieldChange}
              placeholder="ชื่อที่ใช้แสดง"
            />
          </label>

          <div className="devices-actions">
            <button type="submit" className="devices-primary-btn">
              {editingId ? 'อัปเดตอุปกรณ์' : 'สร้างอุปกรณ์'}
            </button>
            <button type="button" className="devices-secondary-btn" onClick={handleReset}>
              รีเซ็ต
            </button>
          </div>

          {saveState.message ? (
            <p className={`devices-feedback devices-feedback-${saveState.type}`}>{saveState.message}</p>
          ) : null}
        </form>

        <div className="devices-card devices-list-card">
          <div className="devices-card-header">
            <div>
              <h2>อุปกรณ์ที่ลงทะเบียนแล้ว</h2>
              <p>เลือกอุปกรณ์เพื่อตรวจสอบรายละเอียด สถานะการมอบหมาย หรือการดำเนินการบำรุงรักษา</p>
            </div>
          </div>

          <div className="devices-filters">
            {['ทั้งหมด', ...DEVICE_STATUSES].map((option) => (
              <button
                key={option}
                type="button"
                className={`devices-filter-chip ${statusFilter === option ? 'devices-filter-chip-active' : ''}`}
                onClick={() => setStatusFilter(option)}
              >
                {option}
              </button>
            ))}
          </div>

          {loadState.type === 'error' ? (
            <div className="devices-empty">
              <strong>ไม่สามารถโหลดอุปกรณ์ได้</strong>
              <p>{loadState.message}</p>
            </div>
          ) : devices.length === 0 ? (
            <div className="devices-empty">
              <strong>ยังไม่มีอุปกรณ์</strong>
              <p>สร้างเครื่องสแกนหรือเครื่องชั่งเครื่องแรกของคุณจากแบบฟอร์ม</p>
            </div>
          ) : visibleDevices.length === 0 ? (
            <div className="devices-empty">
              <strong>ไม่มีอุปกรณ์ที่ตรงกัน</strong>
              <p>อุปกรณ์ที่ลงทะเบียนไว้ในปัจจุบันไม่มีสถานะ '{statusFilter}'</p>
            </div>
          ) : (
            <div className="devices-list">
              {visibleDevices.map((device) => {
                const assignment = getAssignmentState(device.deviceId);
                const provisionedKey = provisionedKeys[device.deviceId];
                const isRevealed = Boolean(revealedKeys[device.deviceId]);

                return (
                  <article key={device.deviceId} className="devices-item">
                    <div className="devices-item-top">
                      <div>
                        <h3>{device.name}</h3>
                        <p>{device.deviceId}</p>
                      </div>
                      <div className="devices-pill-row">
                        <span className={`devices-status devices-status-${String(device.deviceStatus).toLowerCase()}`}>
                          {device.deviceStatus}
                        </span>
                        <span className={`devices-assignment devices-assignment-${assignment ? 'assigned' : 'unassigned'}`}>
                          {getAssignmentLabel(assignment)}
                        </span>
                      </div>
                    </div>

                    <div className="devices-meta">
                      <span>{device.deviceType}</span>
                      <span>{getAssignmentDescription(assignment)}</span>
                      <span>คีย์นำหน้า: {device.authKeyPrefix || 'ยังไม่ได้จัดเตรียม'}</span>
                      <div className="devices-key-row">
                        <span>คีย์อุปกรณ์: <code>{isRevealed ? (provisionedKey ?? 'ไม่สามารถใช้งานได้ในเซสชันนี้') : maskProvisionedKey(provisionedKey)}</code></span>
                        {provisionedKey ? (
                          <button
                            type="button"
                            className="devices-link-btn"
                            onClick={() => toggleKeyReveal(device.deviceId)}
                          >
                            {isRevealed ? 'ซ่อนรหัส' : 'แสดงรหัส'}
                          </button>
                        ) : null}
                      </div>
                      <span>การเข้าสู่ระบบล่าสุด: {formatDate(device.lastAuthenticatedAtUtc)}</span>
                    </div>

                    <div className="devices-item-actions">
                      <button type="button" className="devices-secondary-btn" onClick={() => handleEdit(device)}>
                        แก้ไข
                      </button>
                      <button type="button" className="devices-secondary-btn" onClick={() => handleRotateKey(device.deviceId)}>
                        หมุนรหัส
                      </button>
                      <button
                        type="button"
                        className="devices-danger-btn"
                        onClick={() => handleDelete(device.deviceId)}
                      >
                        ลบ
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>
      )}
    </section>
  );
}
